import React, { useMemo } from 'react';

const formatMinutes = (mins) => {
  if (!mins) return '0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatDayLabel = (isoDate) => {
  const d = new Date(`${isoDate}T00:00:00`);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

// Dynamic stats strip - only cards whose value is truthy (non-zero) are shown, so e.g. a week
// with no manga activity simply omits the manga cards instead of showing "0".
const STAT_CONFIG = [
  { key: 'items_added', label: 'Items Added', icon: 'plus-solid.svg' },
  { key: 'achievements_unlocked', label: 'Achievements Unlocked', icon: 'trophy-solid.svg' },
  { key: 'perfect_games', label: 'Perfect Games', icon: 'crown-solid.svg' },
  { key: 'games_started', label: 'Games Started', icon: 'gamepad-solid.svg' },
  { key: 'games_finished', label: 'Games Finished', icon: 'gamepad-solid.svg' },
  { key: 'game_minutes_played', label: 'Time Played', icon: 'clock-solid.svg', format: formatMinutes, isText: true },
  { key: 'anime_started', label: 'Anime Started', icon: 'tv-solid.svg' },
  { key: 'anime_finished', label: 'Anime Finished', icon: 'tv-solid.svg' },
  { key: 'anime_episodes_watched', label: 'Episodes Watched', icon: 'tv-solid.svg' },
  { key: 'shows_started', label: 'Shows Started', icon: 'clapperboard-solid.svg' },
  { key: 'shows_finished', label: 'Shows Finished', icon: 'clapperboard-solid.svg' },
  { key: 'season_renewals', label: 'Season Renewals', icon: 'arrows-rotate-solid.svg' },
  { key: 'manga_started', label: 'Manga Started', icon: 'book-open-solid-full.svg' },
  { key: 'manga_finished', label: 'Manga Finished', icon: 'book-open-solid-full.svg' },
  { key: 'manga_chapters_read', label: 'Chapters Read', icon: 'book-open-solid-full.svg' },
  { key: 'book_pages_read', label: 'Pages Read', icon: 'book-open-solid.svg' },
];

const TOP_MEDIA_CONFIG = {
  top_game: { label: 'Most Played', icon: 'gamepad-solid.svg', formatAmount: (a) => `${Math.round(a)} min played` },
  top_anime: { label: 'Most Watched', icon: 'tv-solid.svg', formatAmount: (a) => `${a} episode${a === 1 ? '' : 's'} watched` },
  top_manga: { label: 'Most Read', icon: 'book-open-solid-full.svg', formatAmount: (a) => `${a} chapter${a === 1 ? '' : 's'} read` },
  top_book: { label: 'Most Read', icon: 'book-solid.svg', formatAmount: (a) => `${a} page${a === 1 ? '' : 's'} read` },
};

const PeriodStats = ({ stats, topMedia, timeline, period }) => {
  const heading = period?.kind === 'monthly' ? "This Month's Numbers" : "This Week's Numbers";

  // A couple of extra "fun" stats derived client-side from the timeline, since the raw event
  // count and busiest day aren't meaningful enough on their own to warrant a dedicated API field.
  const derived = useMemo(() => {
    if (!timeline || timeline.length === 0) return [];
    const totalActivities = timeline.reduce((sum, day) => sum + day.items.length, 0);
    const busiest = timeline.reduce((max, day) => (day.items.length > (max?.items.length || 0) ? day : max), null);
    const extra = [];
    if (totalActivities) {
      extra.push({ key: 'total_activities', label: 'Total Activity Events', icon: 'list-solid.svg', value: totalActivities });
    }
    if (busiest && busiest.items.length > 0) {
      extra.push({
        key: 'busiest_day',
        label: 'Busiest Day',
        icon: 'calendar-solid.svg',
        value: `${formatDayLabel(busiest.date)} (${busiest.items.length})`,
        isText: true,
      });
    }
    return extra;
  }, [timeline]);

  const visible = STAT_CONFIG.filter(({ key }) => Boolean(stats?.[key]));
  const topMediaEntries = Object.entries(topMedia || {}).filter(([key]) => TOP_MEDIA_CONFIG[key]);

  if (visible.length === 0 && derived.length === 0 && topMediaEntries.length === 0) {
    return null;
  }

  return (
    <section className="period-summary-section period-summary-stats">
      <h2>{heading}</h2>
      {(visible.length > 0 || derived.length > 0) && (
        <div className="period-summary-stats-grid">
          {visible.map(({ key, label, icon, format, isText }) => (
            <div className="period-summary-stat-card" key={key}>
              <img src={`/static/icons/${icon}`} alt="" className="icon" />
              <span className={`period-summary-stat-value${isText ? ' period-summary-stat-value--text' : ''}`}>
                {format ? format(stats[key]) : stats[key]}
              </span>
              <span className="period-summary-stat-label">{label}</span>
            </div>
          ))}
          {derived.map(({ key, label, icon, value, isText }) => (
            <div className="period-summary-stat-card" key={key}>
              <img src={`/static/icons/${icon}`} alt="" className="icon" />
              <span className={`period-summary-stat-value${isText ? ' period-summary-stat-value--text' : ''}`}>{value}</span>
              <span className="period-summary-stat-label">{label}</span>
            </div>
          ))}
        </div>
      )}
      {topMediaEntries.length > 0 && (
        <div className="period-summary-top-media-grid">
          {topMediaEntries.map(([key, media]) => {
            const config = TOP_MEDIA_CONFIG[key];
            return (
              <div className="period-summary-top-media-card" key={key}>
                <img src={media.image} alt={media.title} className="period-summary-top-media-image" />
                <div className="period-summary-top-media-info">
                  <span className="period-summary-top-media-label">
                    <img src={`/static/icons/${config.icon}`} alt="" className="icon" />
                    {config.label}
                  </span>
                  <span className="period-summary-top-media-title">{media.title}</span>
                  <span className="period-summary-top-media-amount">{config.formatAmount(media.amount)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default PeriodStats;
